import { t, type MessageKey } from '@reos/i18n';
import type { LeadDto, TerritoryDto } from '../../lib/acquisition-radar-api.js';

/**
 * Krok AKV1 — popisky kodu radaru pro makléře. Kody signalu, kategorii, typu
 * obchodu a sablon jsou DATA radaru (ne enumy REOS, viz `api-schemas.ts`
 * kontextu): znamy kod dostane cesky popisek z katalogu, neznamy se ukaze
 * tak, jak ho radar poslal — novy signal radaru tak nikdy nezmizi z obrazovky.
 */

const STATUS_KEYS: Readonly<Record<LeadDto['status'], MessageKey>> = {
  new: 'webAdmin.acquisitionRadar.status.new',
  // Postup maklere (stavy radaru od kroku C10).
  contacted: 'webAdmin.acquisitionRadar.status.contacted',
  meeting: 'webAdmin.acquisitionRadar.status.meeting',
  won: 'webAdmin.acquisitionRadar.status.won',
  claimed: 'webAdmin.acquisitionRadar.status.claimed',
  letter_approved: 'webAdmin.acquisitionRadar.status.letterApproved',
  rejected: 'webAdmin.acquisitionRadar.status.rejected',
  unknown: 'webAdmin.acquisitionRadar.status.unknown',
};

/** Druhy signalu radaru (`akv/signals.py`). */
export const SIGNAL_KEYS: Readonly<Record<string, MessageKey>> = {
  neprodalo_se: 'webAdmin.acquisitionRadar.signal.unsold',
  prodano: 'webAdmin.acquisitionRadar.signal.sold',
  vyhorely_inzerat: 'webAdmin.acquisitionRadar.signal.stale',
  // Krok MRGA1: dnes PREVAZUJICI signal zivého radaru (bez popisku by makléř
  // v seznamu i ve filtru cetl technicky kod).
  soukromy_inzerat: 'webAdmin.acquisitionRadar.signal.private',
};

/** Sablony dopisu radaru (`dopis.texty.TEMPLATES`). */
const TEMPLATE_KEYS: Readonly<Record<string, MessageKey>> = {
  neprodano: 'webAdmin.acquisitionRadar.template.unsold',
  dlouho: 'webAdmin.acquisitionRadar.template.stale',
  just_sold: 'webAdmin.acquisitionRadar.template.justSold',
  // Sablona k signalu `soukromy_inzerat` (`SIGNAL_KIND_TO_SABLONA` v radaru).
  soukromy: 'webAdmin.acquisitionRadar.template.private',
};

/** Kategorie inzeratu, jak je radar normalizuje z portalu. */
export const CATEGORY_KEYS: Readonly<Record<string, MessageKey>> = {
  byt: 'webAdmin.acquisitionRadar.category.flat',
  dum: 'webAdmin.acquisitionRadar.category.house',
  pozemek: 'webAdmin.acquisitionRadar.category.land',
  komercni: 'webAdmin.acquisitionRadar.category.commercial',
  jine: 'webAdmin.acquisitionRadar.category.other',
};

export const DEAL_TYPE_KEYS: Readonly<Record<string, MessageKey>> = {
  prodej: 'webAdmin.acquisitionRadar.dealType.sale',
  pronajem: 'webAdmin.acquisitionRadar.dealType.rent',
};

/**
 * Zdroje, ktere radar dnes cte (README konektoru). Katalog je tu, protoze ho
 * potrebuje filtr zdroje i sloupec „Zdroj" v seznamu tipu (krok MRGA1) —
 * neznamy portal se ukaze tak, jak ho radar poslal.
 */
export const SOURCE_KEYS: Readonly<Record<string, MessageKey>> = {
  sreality: 'webAdmin.acquisitionRadar.source.sreality',
  idnes: 'webAdmin.acquisitionRadar.source.idnes',
  bazos: 'webAdmin.acquisitionRadar.source.bazos',
  facebook: 'webAdmin.acquisitionRadar.source.facebook',
};

const KIND_KEYS: Readonly<Record<TerritoryDto['kind'], MessageKey>> = {
  municipality: 'webAdmin.acquisitionRadar.kind.municipality',
  municipality_part: 'webAdmin.acquisitionRadar.kind.municipalityPart',
  orp: 'webAdmin.acquisitionRadar.kind.orp',
  district: 'webAdmin.acquisitionRadar.kind.district',
  radius: 'webAdmin.acquisitionRadar.kind.radius',
  polygon: 'webAdmin.acquisitionRadar.kind.polygon',
};

function labelOf(keys: Readonly<Record<string, MessageKey>>, code: string | null): string {
  if (code === null || code === '') return t('webAdmin.acquisitionRadar.unknownValue', { locale: 'cs' });
  const key = keys[code];
  return key === undefined ? code : t(key, { locale: 'cs' });
}

export const statusLabel = (status: LeadDto['status']): string => t(STATUS_KEYS[status], { locale: 'cs' });
export const signalLabel = (code: string | null): string => labelOf(SIGNAL_KEYS, code);
export const templateLabel = (code: string): string => labelOf(TEMPLATE_KEYS, code);
export const categoryLabel = (code: string | null): string => labelOf(CATEGORY_KEYS, code);
export const sourceLabel = (code: string): string => labelOf(SOURCE_KEYS, code);
export const dealTypeLabel = (code: string | null): string => labelOf(DEAL_TYPE_KEYS, code);
export const kindLabel = (kind: TerritoryDto['kind']): string => t(KIND_KEYS[kind], { locale: 'cs' });

const DATE = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });

export function formatDate(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DATE.format(date);
}

export function formatCzk(amount: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(value);
}
